import React, { useEffect, useState, useCallback, useRef } from 'react';
import { getOrders, getOrder } from '../services/api';
import { imageUrl } from '../utils/image';
import { format } from 'date-fns';
import { el } from 'date-fns/locale';
import toast from 'react-hot-toast';
import StatusTimeline from '../components/StatusTimeline';

// Read-only board for the Οδηγός role. The backend decides which orders he
// may see (his factories OR his shops) and refuses him anything that is not
// a GET, so this page deliberately offers no action of any kind.

const STATUS_LABELS = {
  NEW: 'Νέα',
  IN_PROGRESS: 'Σε εξέλιξη',
  READY: 'Έτοιμη',
  DELIVERED: 'Παραδόθηκε',
  CANCELLED: 'Ακυρώθηκε',
};
const STATUS_BADGE = {
  NEW: 'badge-blue',
  IN_PROGRESS: 'badge-amber',
  READY: 'badge-green',
  DELIVERED: 'badge-gray',
  CANCELLED: 'badge-red',
};
const STATUS_ICON = {
  NEW: '🆕', IN_PROGRESS: '⚙️', READY: '✅', DELIVERED: '📦', CANCELLED: '❌',
};

const POLL_MS = 20000;
const PAGE_SIZE = 100;

// The date columns are TIMESTAMP WITHOUT TIME ZONE holding Greek wall-clock
// time, so filter bounds go out as plain local strings. toISOString() would
// shift every boundary by the UTC offset.
const naive = (d) => format(d, "yyyy-MM-dd'T'HH:mm:ss");
const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };

const dateStartOf = (key) => {
  const now = new Date();
  if (key === 'today') return naive(startOfDay(now));
  if (key === 'yesterday') { const d = startOfDay(now); d.setDate(d.getDate() - 1); return naive(d); }
  if (key === 'last7') { const d = startOfDay(now); d.setDate(d.getDate() - 6); return naive(d); }
  if (key === 'month') return naive(new Date(now.getFullYear(), now.getMonth(), 1));
  return null;
};
const dateEndOf = (key) => {
  const now = new Date();
  if (key === 'yesterday') return naive(startOfDay(now));
  return null;
};

const fmt = (d, pattern) => {
  if (!d) return '';
  try { return format(new Date(d), pattern, { locale: el }); } catch { return ''; }
};

export default function DriverOrders() {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [dateRange, setDateRange] = useState('today');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [dateField, setDateField] = useState('created_at');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const [expandedId, setExpandedId] = useState(null);
  const [details, setDetails] = useState({});
  const [lightbox, setLightbox] = useState(null);

  const inFlightRef = useRef(false);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => { setPage(1); }, [statusFilter, dateRange, customFrom, customTo, dateField, search]);

  const load = useCallback((silent = false) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    if (!silent) setLoading(true);

    const params = { page, page_size: PAGE_SIZE, date_field: dateField };
    if (statusFilter) params.status = statusFilter;
    if (search) params.search = search;
    if (dateRange === 'custom') {
      if (customFrom) params.from = `${customFrom}T00:00:00`;
      if (customTo) params.to = `${customTo}T23:59:59`;
    } else {
      const from = dateStartOf(dateRange);
      const to = dateEndOf(dateRange);
      if (from) params.from = from;
      if (to) params.to = to;
    }

    getOrders(params)
      .then((r) => {
        const data = r.data;
        if (Array.isArray(data)) { setOrders(data); setTotal(data.length); }
        else { setOrders(data.orders || []); setTotal(data.total || 0); }
      })
      .catch((err) => { if (!silent) toast.error(err.response?.data?.error || 'Σφάλμα φόρτωσης'); })
      .finally(() => { inFlightRef.current = false; if (!silent) setLoading(false); });
  }, [page, statusFilter, dateRange, customFrom, customTo, dateField, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = setInterval(() => load(true), POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  const toggle = async (o) => {
    if (expandedId === o.id) { setExpandedId(null); return; }
    setExpandedId(o.id);
    if (details[o.id]) return;
    try {
      const r = await getOrder(o.id);
      setDetails((d) => ({ ...d, [o.id]: r.data }));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Σφάλμα');
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <>
      <div className="page-header">
        <div className="flex-between">
          <div>
            <h2>🚚 Παραγγελίες</h2>
            <p>Προβολή μόνο — τα εργοστάσια και τα καταστήματά σας</p>
          </div>
        </div>

        {/* Filters. Wrapping is what makes this usable on a phone: each
            control keeps a comfortable tap size and drops to its own row. */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
          <input
            className="form-input"
            placeholder="🔍 Πελάτης ή #αριθμός"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={{ flex: '1 1 180px', minWidth: 150, padding: '8px 12px', margin: 0 }}
          />
          <select className="form-select" value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ flex: '0 1 150px', padding: '8px 10px', margin: 0 }}>
            <option value="">Όλες οι καταστάσεις</option>
            {Object.keys(STATUS_LABELS).map((k) => (
              <option key={k} value={k}>{STATUS_ICON[k]} {STATUS_LABELS[k]}</option>
            ))}
          </select>
          <select className="form-select" value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            style={{ flex: '0 1 150px', padding: '8px 10px', margin: 0 }}>
            <option value="today">Σήμερα</option>
            <option value="yesterday">Χθες</option>
            <option value="last7">Τελευταίες 7</option>
            <option value="month">Αυτόν τον μήνα</option>
            <option value="all">Όλες</option>
            <option value="custom">Επιλογή ημερομηνιών</option>
          </select>
          <select className="form-select" value={dateField}
            onChange={(e) => setDateField(e.target.value)}
            style={{ flex: '0 1 165px', padding: '8px 10px', margin: 0 }}>
            <option value="created_at">Ημ. καταχώρησης</option>
            <option value="requested_for">Ημ. παραλαβής</option>
          </select>
          {dateRange === 'custom' && (
            <>
              <input type="date" className="form-input" value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                style={{ flex: '0 1 150px', padding: '8px 10px', margin: 0 }} />
              <input type="date" className="form-input" value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                style={{ flex: '0 1 150px', padding: '8px 10px', margin: 0 }} />
            </>
          )}
        </div>
      </div>

      <div className="page-body">
        {loading ? (
          <div className="page-loading"><span className="loading-spinner dark" /></div>
        ) : orders.length === 0 ? (
          <div className="card"><div className="card-body">
            <div className="empty-state"><div>🚚</div><p>Καμία παραγγελία για αυτό το διάστημα</p></div>
          </div></div>
        ) : (
          <>
            <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 10 }}>
              {total} {total === 1 ? 'παραγγελία' : 'παραγγελίες'}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {orders.map((o) => {
                const d = details[o.id];
                const isOpen = expandedId === o.id;
                return (
                  <div key={o.id} className="card">
                    {/* Header row — the whole strip is the tap target, which
                        matters far more on a phone than a small chevron */}
                    <button
                      onClick={() => toggle(o)}
                      style={{
                        width: '100%', textAlign: 'left', background: 'transparent',
                        border: 'none', padding: '12px 14px', cursor: 'pointer',
                        display: 'flex', alignItems: 'flex-start', gap: 10,
                        font: 'inherit', color: 'inherit',
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, color: 'var(--gray-900)', fontSize: 15 }}>
                          #{o.id} {o.customer_name || '(χωρίς όνομα)'}
                        </div>
                        <div style={{ fontSize: 12.5, color: 'var(--gray-500)', marginTop: 3, lineHeight: 1.5 }}>
                          🏪 {o.shop_name}
                          {o.requested_for && <> · 🕒 {fmt(o.requested_for, 'd MMM, HH:mm')}</>}
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
                        <span className={'badge ' + (STATUS_BADGE[o.status] || 'badge-gray')}>
                          {STATUS_LABELS[o.status] || o.status}
                        </span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--blue-dark)' }}>
                          €{parseFloat(o.grand_total || 0).toFixed(2)}
                        </span>
                      </div>
                      <span style={{ color: 'var(--gray-400)', fontSize: 13, flexShrink: 0, marginTop: 2 }}>
                        {isOpen ? '▾' : '▸'}
                      </span>
                    </button>

                    {isOpen && (
                      <div style={{ padding: '0 14px 14px', borderTop: '1px solid var(--gray-100)' }}>
                        {!d ? (
                          <div className="page-loading" style={{ padding: 24 }}>
                            <span className="loading-spinner dark" />
                          </div>
                        ) : (
                          <>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 13, color: 'var(--gray-600)', margin: '12px 0' }}>
                              {d.customer_phone && (
                                <div>📞 <a href={`tel:${d.customer_phone}`} style={{ color: 'var(--blue-dark)' }}>{d.customer_phone}</a></div>
                              )}
                              <div>📅 Καταχώρηση: {fmt(d.created_at, 'd MMM yyyy, HH:mm')}</div>
                              {d.created_by_worker_name && <div>🧑 {d.created_by_worker_name}</div>}
                            </div>

                            {d.customer_notes && (
                              <div style={{
                                background: '#fffbeb', border: '1px solid #fde68a',
                                borderRadius: 8, padding: '8px 10px', fontSize: 13,
                                color: '#92400e', marginBottom: 12,
                              }}>
                                📝 {d.customer_notes}
                              </div>
                            )}

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
                              {(d.items || []).map((it) => (
                                <div key={it.id} style={{
                                  display: 'flex', alignItems: 'flex-start', gap: 10,
                                  padding: '8px 10px', borderRadius: 8,
                                  background: it.status === 'READY' ? '#f0fdf4' : 'var(--gray-50)',
                                  border: '1px solid var(--gray-100)',
                                }}>
                                  <span style={{ fontSize: 14, flexShrink: 0 }}>
                                    {it.status === 'READY' ? '✅' : '⬜'}
                                  </span>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 14, color: 'var(--gray-800)' }}>
                                      <strong>{it.quantity}×</strong> {it.product_name}
                                    </div>
                                    <div style={{ fontSize: 11.5, color: 'var(--gray-500)', marginTop: 2 }}>
                                      🏭 {it.factory_name || '—'}
                                      {it.ready_by_worker_name && <> · {it.ready_by_worker_name}</>}
                                      {it.notes && <> · 📝 {it.notes}</>}
                                    </div>
                                  </div>
                                  <div style={{ fontSize: 13, color: 'var(--gray-600)', flexShrink: 0 }}>
                                    €{parseFloat(it.total_price || 0).toFixed(2)}
                                  </div>
                                </div>
                              ))}
                            </div>

                            {(d.images || []).length > 0 && (
                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                                {d.images.map((img) => (
                                  <button key={img.id} onClick={() => setLightbox(imageUrl(img.access_key))}
                                    style={{ border: 'none', padding: 0, background: 'none', cursor: 'pointer' }}>
                                    <img src={imageUrl(img.access_key)} alt=""
                                      style={{ width: 76, height: 76, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--gray-200)' }} />
                                  </button>
                                ))}
                              </div>
                            )}

                            <StatusTimeline currentStatus={d.status} history={d.history || []} />
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 16 }}>
                <button className="btn btn-secondary" disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}>← Προηγ.</button>
                <span style={{ fontSize: 13, color: 'var(--gray-500)' }}>{page} / {totalPages}</span>
                <button className="btn btn-secondary" disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Επόμ. →</button>
              </div>
            )}
          </>
        )}
      </div>

      {lightbox && (
        <div onClick={() => setLightbox(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 2000, padding: 16,
          }}>
          <img src={lightbox} alt="" style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 8 }} />
        </div>
      )}
    </>
  );
}
